import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastId = 0;

const TOAST_DELAYS: Record<ToastType, number> = {
  success: 5000,
  error: 8000,
  warning: 6000,
  info: 5000,
};

const TOAST_STYLES: Record<ToastType, { bg: string; headerBg: string; icon: string }> = {
  success: { bg: 'bg-success', headerBg: 'bg-success text-white', icon: '\u2713' },
  error: { bg: 'bg-danger', headerBg: 'bg-danger text-white', icon: '\u2717' },
  warning: { bg: 'bg-warning', headerBg: 'bg-warning', icon: '\u26A0' },
  info: { bg: 'bg-info', headerBg: 'bg-info text-white', icon: '\u2139' },
};

const TOAST_TITLES: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {toasts.map((toast) => {
          const style = TOAST_STYLES[toast.type];
          return (
            <Toast
              key={toast.id}
              onClose={() => removeToast(toast.id)}
              delay={TOAST_DELAYS[toast.type]}
              autohide
            >
              <Toast.Header className={style.headerBg}>
                <strong className="me-auto">
                  {style.icon} {TOAST_TITLES[toast.type]}
                </strong>
              </Toast.Header>
              <Toast.Body className={toast.type === 'error' || toast.type === 'success' ? 'text-dark' : ''}>
                {toast.message}
              </Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
